import { Float, MeshDistortMaterial, Text3D, Center } from '@react-three/drei';
import React, { useEffect, useRef, useState } from 'react';
import YootButtonModel from '../meshes/YootButtonModel';
import { useFrame } from '@react-three/fiber';
import Cursor from '../meshes/Cursor';
import Mars from '../meshes/Mars';
import Star from '../meshes/Stars/Star';
import layout from '../dictionaries/layout';
import Rocket from '../meshes/Rocket';
import Pointer from '../meshes/Pointer';
import { useSpring, animated } from '@react-spring/three';
import * as THREE from 'three';
import Ufo from '../meshes/Ufo';
import YootSet from '../meshes/YootSet';
import GulToken from '../moveTokens/GulToken';
import GeToken from '../moveTokens/GeToken';
import Board from './Board';
import YootDisplay from './YootDisplay';
import { useFireworksShader } from '../shader/fireworks/FireworksShader';
import YootNew from './YootNew';
import { useAtomValue } from 'jotai';
import { deviceAtom, languageAtom } from '../GlobalState';
import translations from '../dictionaries/translations';

export default function HowToPlay({ 
  position=[0,0,0], 
  rotation=[0,0,0], 
  scale=1,
  closeButton=false,
  setShowRulebook=null,
  tabOrientation='bottom',
}) {
  
  const pageTimeoutRef = useRef(null)
  const [tabClicked, setTabClicked] = useState(false)
  const [page, setPage] = useState(0)
  const device = useAtomValue(deviceAtom)
  const language = useAtomValue(languageAtom)

  useEffect(() => {
    clearTimeout(pageTimeoutRef.current)
    if (!tabClicked) {
      // page 0 timeout is set on component load
      if (page === 0) {
        const page1Timeout = setTimeout(() => {
          setPage(1)
        }, 23500)
        // use useRef to not re-render while setting a state
        pageTimeoutRef.current = page1Timeout
      } else if (page === 1) { // Throw the dice
        const page2Timeout = setTimeout(() => {
          setPage(2)
        }, 10700)
        pageTimeoutRef.current = page2Timeout
      } else if (page === 2) { // Read the Yut
        const page3Timeout = setTimeout(() => {
          setPage(3)
        }, 11500)
        pageTimeoutRef.current = page3Timeout
      } else if (page === 3) { // Movement 
        const page4Timeout = setTimeout(() => {
          setPage(4)
        }, 14000)
        pageTimeoutRef.current = page4Timeout
      } else if (page === 4) { // Catch enemies 
        const page5Timeout = setTimeout(() => {
          setPage(5)
        }, 8500)
        pageTimeoutRef.current = page5Timeout
      } else if (page === 5) { // Piggyback
        const page6Timeout = setTimeout(() => {
          setPage(6)
        }, 5700)
        pageTimeoutRef.current = page6Timeout
      } else if (page === 6) { // Winning
        const page7Timeout = setTimeout(() => {
          setPage(7)
        }, 9000)
        pageTimeoutRef.current = page7Timeout
      } else if (page === 7) { // Tips
        const page8Timeout = setTimeout(() => {
          setPage(0)
        }, 12000)
        pageTimeoutRef.current = page8Timeout
      }
      
    }
  }, [page])
  
  const ANIMATION = 17
  
  function Overview() {
    const TILE_RADIUS = 5
    const scorePositionShift = [-4.5, 0, 1]
    const springs = useSpring({
      from: {
        catchTokenPosition: [
          -Math.cos(((12) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          1.5,
          Math.sin(((12) * (Math.PI * 2)) / 20) * TILE_RADIUS,
        ],
        catchTokenScale: 1.5,
        piggybackTokenPosition: [-4.1,1,-3.1],
        movingTokenPosition: [-4.3,0,6.5],
        movingTokenScale: 1,
        catchAlertScale: 0,
        catchTokenHomeScale: 0,
        piggybackAlertScale: 0,
        piggybackTokenScale: 1.5,
        welcomeHomeAlertScale: 0,
        scoredIndicator0Scale: 0,
        scoredIndicator1Scale: 0,
      },
      to: [
        {
          movingTokenPosition: [
            -Math.cos(((5) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            2.5,
            Math.sin(((5) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          movingTokenScale: 1.4,
        },
        {
          movingTokenPosition: [
            -Math.cos(((6) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((6) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((7) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((7) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((8) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((8) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((9) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((9) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((10) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((10) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((11) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((11) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((12) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((12) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          catchAlertScale: 0.7,
        },
        {
          catchTokenPosition: [4.5, 0, 5.5],
          catchTokenScale: 0,
          delay: 500
        },
        {
          catchTokenHomeScale: 1,
        },
        {
          movingTokenPosition: [
            -Math.cos(((13) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((13) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          catchAlertScale: 0
        },
        {
          movingTokenPosition: [
            -Math.cos(((14) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((14) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((15) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((15) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((16) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((16) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((17) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((17) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((18) * (Math.PI * 2)) / 20) * TILE_RADIUS,
            1.5,
            Math.sin(((18) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackAlertScale: 0.7
        },
        {
          movingTokenPosition: [
            -Math.cos(((18) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((18) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((18) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((18) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenScale: 1.4,
          delay: 500
        },
        {
          movingTokenPosition: [
            -Math.cos(((19) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((19) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((19) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((19) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackAlertScale: 0
        },
        {
          movingTokenPosition: [
            -Math.cos(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5,
          ],
          piggybackTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5,
          ],
          welcomeHomeAlertScale: 1
        },
        {
          movingTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5 + scorePositionShift[0],
            1.5 + scorePositionShift[1],
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5 + scorePositionShift[2],
          ],
          piggybackTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5 + scorePositionShift[0],
            1.5 + scorePositionShift[1],
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5 + scorePositionShift[2],
          ],
          movingTokenScale: 0,
          piggybackTokenScale: 0,
          welcomeHomeAlertScale: 0,
          delay: 500
        },
        {
          scoredIndicator0Scale: 0.4,
          scoredIndicator1Scale: 0.4,
        }
      ],
      delay: 1000,
      config: {
        tension: 170,
        friction: 26
      }
    })

    function CatchAlert({ position, scale }) {
      
      const borderMesh0Ref = useRef();
      const borderMesh1Ref = useRef();
      const borderMesh2Ref = useRef();
      const borderMesh3Ref = useRef();
      const borderMesh4Ref = useRef();
      const borderMesh5Ref = useRef();
      const borderMesh6Ref = useRef();
      const borderMeshRefs = [
        borderMesh0Ref,
        borderMesh1Ref,
        borderMesh2Ref,
        borderMesh3Ref,
        borderMesh4Ref,
        borderMesh5Ref,
        borderMesh6Ref
      ]

      const height = 1
      const width = 2
      const starScale = 0.1
      useFrame((state) => {
        const time = state.clock.elapsedTime 
        for (let i = 0; i < borderMeshRefs.length; i++) {      
          if (borderMeshRefs[i].current) {
            borderMeshRefs[i].current.position.x = Math.cos(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * width
            borderMeshRefs[i].current.position.y = 0.05
            borderMeshRefs[i].current.position.z = Math.sin(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * height
          }
        }
      })

      return <animated.group position={position} scale={scale}>
        <mesh scale={[width, 0.01, height]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={0.3}/>
        </mesh>
        <Text3D
        name='main-text'
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-1.29,0,0.25]}
        rotation={[-Math.PI/2,0,0]}
        size={0.6}
        height={0.01}>
          {translations.howToPlayHome.catch[language]}
          <meshStandardMaterial color='limegreen'/>
        </Text3D>
        <group ref={borderMesh0Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh1Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh2Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh3Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh4Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh5Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh6Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
      </animated.group>
    }

    function PiggybackAlert({ position, scale }) {
      
      const borderMesh0Ref = useRef();
      const borderMesh1Ref = useRef();
      const borderMesh2Ref = useRef();
      const borderMesh3Ref = useRef();
      const borderMesh4Ref = useRef();
      const borderMesh5Ref = useRef();
      const borderMesh6Ref = useRef();
      const borderMeshRefs = [
        borderMesh0Ref,
        borderMesh1Ref,
        borderMesh2Ref,
        borderMesh3Ref,
        borderMesh4Ref,
        borderMesh5Ref,
        borderMesh6Ref
      ]

      const height = 1
      const width = 2.5
      const starScale = 0.1
      useFrame((state) => {
        const time = state.clock.elapsedTime 
        for (let i = 0; i < borderMeshRefs.length; i++) {      
          if (borderMeshRefs[i].current) {
            borderMeshRefs[i].current.position.x = Math.cos(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * width
            borderMeshRefs[i].current.position.y = 0.05
            borderMeshRefs[i].current.position.z = Math.sin(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * height
          }
        }
      })

      return <animated.group position={position} scale={scale}>
        <mesh scale={[width, 0.01, height]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={0.3}/>
        </mesh>
        <Text3D
        name='main-text'
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-1.8,0,0.2]}
        rotation={layout[device].game.whoGoesFirst.title.rotation}
        size={0.5}
        height={layout[device].game.whoGoesFirst.title.height}>
          {translations.howToPlayHome.piggyback[language]}
          <meshStandardMaterial color='limegreen'/>
        </Text3D>
        <group ref={borderMesh0Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh1Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh2Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh3Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh4Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh5Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh6Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
      </animated.group>
    }

    function WelcomeHomeAlert({ position, scale }) {
      
      const borderMesh0Ref = useRef();
      const borderMesh1Ref = useRef();
      const borderMesh2Ref = useRef();
      const borderMesh3Ref = useRef();
      const borderMesh4Ref = useRef();
      const borderMesh5Ref = useRef();
      const borderMesh6Ref = useRef();
      const borderMeshRefs = [
        borderMesh0Ref,
        borderMesh1Ref,
        borderMesh2Ref,
        borderMesh3Ref,
        borderMesh4Ref,
        borderMesh5Ref,
        borderMesh6Ref
      ]

      const height = 1.3
      const width = 2.4
      const starScale = 0.12
      useFrame((state) => {
        const time = state.clock.elapsedTime 
        for (let i = 0; i < borderMeshRefs.length; i++) {      
          if (borderMeshRefs[i].current) {
            borderMeshRefs[i].current.position.x = Math.cos(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * width
            borderMeshRefs[i].current.position.y = 0.05
            borderMeshRefs[i].current.position.z = Math.sin(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * height
          }
        }
      })

      return <animated.group position={position} scale={scale}>
        <mesh scale={[width, 0.01, height]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={0.8}/>
        </mesh>
        <Text3D
        name='main-text'
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-1.45,0,-0.1]}
        rotation={layout[device].game.whoGoesFirst.title.rotation}
        size={0.5}
        height={layout[device].game.whoGoesFirst.title.height}
        lineHeight={0.8}>
          {translations.howToPlayHome.welcomeHome[language]}
          <meshStandardMaterial color='limegreen'/>
        </Text3D>
        <group ref={borderMesh0Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh1Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh2Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh3Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh4Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh5Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
        <group ref={borderMesh6Ref}>
          <Star 
            scale={starScale}
            color='limegreen'
          />
        </group>
      </animated.group>
    }

    return <group>
      <CatchAlert position={[7.5, 1, -5]} scale={springs.catchAlertScale}/>
      <PiggybackAlert position={[-1.5, 1, -5]} scale={springs.piggybackAlertScale}/>
      <WelcomeHomeAlert position={[2.8,1,2]} scale={springs.welcomeHomeAlertScale}/>
      <group name='scene' scale={layout[device].howToPlay.overviewPage.scene.scale} position={layout[device].howToPlay.overviewPage.scene.position}>
        <group name='board' position={[2.8, 0, -0.5]} scale={0.9}>
          <Board showConstellations={false} showStart legalTiles={{}} helperTiles={{}}/>
          {/* Catch token */}
          <animated.group position={springs.catchTokenPosition} scale={springs.catchTokenScale}>
            <Ufo onBoard/>
          </animated.group>
          {/* Piggyback token */}
          <animated.group scale={springs.piggybackTokenScale} position={springs.piggybackTokenPosition}>
            <Rocket onBoard/>
          </animated.group>
          {/* Moving token */}
          <animated.group scale={springs.movingTokenScale} position={springs.movingTokenPosition}>
            <Rocket onBoard/>
          </animated.group>
        </group>
        <group name='ufo-home' position={[7, 0, 5]}>
          <mesh position={[0, -0.5, -0.2]}>
            <cylinderGeometry args={[1.4, 1.4, 0.01, 32]}/>
            <meshStandardMaterial color='turquoise' transparent opacity={0.05}/>
          </mesh>
          <Ufo position={[-0.5,0,-0.4]}/>
          <Ufo position={[0.5,0,-0.4]}/>
          <Ufo position={[-0.5,0,0.4]}/>
          <animated.group scale={springs.catchTokenHomeScale}>
            <Ufo position={[0.5,0,0.4]}/>
          </animated.group>
          {/* Add hologram Ufo */}
        </group>
        <group name='rocket-home' position={[-1.5, 0, 5]}>
          <mesh position={[0, -0.5, -0.2]}>
            <cylinderGeometry args={[1.4, 1.4, 0.01, 32]}/>
            <meshStandardMaterial color='red' transparent opacity={0.1}/>
          </mesh>
          <Rocket position={[-0.6,0,-0.5]}/>
          <Rocket position={[0.4,0,-0.5]}/>
          {/* Moving token */}
          {/* Add hologram Rocket */}
          <animated.group name='scored-indicator-0' position={[-0.5, 0, 0.5]} scale={springs.scoredIndicator0Scale}>
            <Star color='red'/>
          </animated.group>
          <animated.group name='scored-indicator-1' position={[0.5, 0, 0.5]} scale={springs.scoredIndicator1Scale}>
            <Star color='red'/>
          </animated.group>
        </group>
        <group name='yoot-display' position={[4.2, 0, 1.4]}>
          <Text3D 
          name='goal'
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[1,0,-0.5]}
          rotation={layout[device].game.whoGoesFirst.title.rotation}
          size={0.3}
          height={layout[device].game.whoGoesFirst.title.height}>
            {translations.howToPlayHome.yutDice[language]}
            <meshStandardMaterial color='orange'/>
          </Text3D>
          <YootDisplay rotation={[0, Math.PI/2, 0]} scale={0.15} enableHoverEffect={true}/>
        </group>
      </group>
      <Text3D 
      name='goal'
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={layout[device].howToPlay.overviewPage.text.position}
      rotation={layout[device].howToPlay.overviewPage.text.rotation}
      size={layout[device].howToPlay.overviewPage.text.size}
      height={layout[device].howToPlay.overviewPage.text.height}>
        {translations.howToPlayHome.goalDescription[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
    </group>
  }

  function ThrowTheYutPage() {

    const [yootButtonTurnedOn, setYootButtonTurnedOn] = useState(true)
    const [animation, setAnimation] = useState(null)
    const [yutPosition, setYutPosition] = useState(layout[device].howToPlay.throwingTheDicePage.yut.initialYutPosition)
    const [yutRotation, setYutRotation] = useState(layout[device].howToPlay.throwingTheDicePage.yut.initialYutRotation)

    useEffect(() => {
      const animationTimeout = setTimeout(() => {
        setYootButtonTurnedOn(false)
        setYutPosition(layout[device].howToPlay.throwingTheDicePage.yut.animationYutPosition)
        setYutRotation(layout[device].howToPlay.throwingTheDicePage.yut.animationYutRotation)
        setAnimation(ANIMATION)
      }, 1000)

      return () => {
        clearTimeout(animationTimeout)
      }
    }, [])

    const springsText = useSpring({
      from: {
        scale: 0
      },
      to: [
        {
          scale: 1.2,
          config: {
            tension: 180,
            friction: 15
          },
          delay: 5300
        }
      ],
    })

    const yutNewGroup = useRef();
    useFrame((state) => {
      const time = state.clock.elapsedTime
      if (!animation) {
        yutNewGroup.current.position.y = Math.sin(time * 2) * 0.8 + 0.2
        yutNewGroup.current.rotation.y = Math.sin(time * 2) * 0.03
      } else {
        yutNewGroup.current.position.y = 0
        yutNewGroup.current.rotation.y = 0
      }
    })

    const AnimatedMeshDistortMaterial = animated(MeshDistortMaterial)
    const springsSide0 = useSpring({
      from: {
        // opacity: 1
        opacity: 1,
        scale: 0
      },
      to: [
        {
          // opacity: 1,
          scale: 1,
          config: {
            tension: 180,
            friction: 15
          },
          delay: 3800
        }
      ],
    })
    const springsSide1 = useSpring({
      from: {
        // opacity: 1
        opacity: 1,
        scale: 0
      },
      to: [
        {
          // opacity: 1,
          scale: 1,
          config: {
            tension: 180,
            friction: 15
          },
          delay: 4300
        }
      ],
    })
    const springsSide2 = useSpring({
      from: {
        // opacity: 1
        opacity: 1,
        scale: 0
      },
      to: [
        {
          // opacity: 1,
          scale: 1,
          config: {
            tension: 180,
            friction: 15
          },
          delay: 4800
        }
      ],
    })

    return <group name='throw-the-yut-page' scale={layout[device].howToPlay.throwingTheDicePage.scale}>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={layout[device].howToPlay.throwingTheDicePage.text.position}
        rotation={layout[device].howToPlay.throwingTheDicePage.text.rotation}
        size={layout[device].howToPlay.throwingTheDicePage.text.size}
        height={layout[device].howToPlay.throwingTheDicePage.text.height}
      >
        {translations.howToPlayHome.theYutDescription[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <animated.group
        position={layout[device].howToPlay.throwingTheDicePage.moveText.position}
        scale={springsText.scale}
      >
        <Text3D
          rotation={[-Math.PI/2,0,0]}
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"} 
          size={layout[device].howToPlay.throwingTheDicePage.moveText.size} 
          height={0.01}
        >
          {translations.howToPlayHome.jump3Spaces[language]}
          <meshStandardMaterial color={ "limegreen" }/>
        </Text3D>
        <GulToken 
          scale={layout[device].howToPlay.throwingTheDicePage.gulToken.scale} 
          position={layout[device].howToPlay.throwingTheDicePage.gulToken.position} 
          rotation={layout[device].howToPlay.throwingTheDicePage.gulToken.rotation}
        />
      </animated.group>
      <group ref={yutNewGroup}>
        <YootNew animation={animation} scale={0.4} position={yutPosition} rotation={yutRotation}/>
      </group>
      <animated.group name='side-0-highlight' position={[-0.3, 0.6, -0.8]} scale={springsSide0.scale}>
        <mesh scale={[1.1, 0.01, 0.3]} rotation={[0, -Math.PI/128, -Math.PI/32]}>
          <cylinderGeometry args={[1, 1, 1]}/>
          <AnimatedMeshDistortMaterial distort={0.1} color='limegreen' transparent opacity={springsSide0.opacity}/>
        </mesh>
      </animated.group>
      <animated.group name='side-1-highlight' position={[5.6, 0.6, -1.2]} scale={springsSide1.scale}>
        <mesh scale={[1.1, 0.01, 0.3]} rotation={[0, Math.PI/4 - Math.PI/32 - Math.PI/64, -Math.PI/32]}>
          <cylinderGeometry args={[1, 1, 1]}/>
          <AnimatedMeshDistortMaterial distort={0.1} color='limegreen' transparent opacity={springsSide1.opacity}/>
        </mesh>
      </animated.group>
      <animated.group name='side-2-highlight' position={[3.9, 0.6, 1.1]} scale={springsSide2.scale}>
        <mesh scale={[1.1, 0.01, 0.3]} rotation={[0, -Math.PI/8 + Math.PI/16, -Math.PI/32]}>
          <cylinderGeometry args={[1, 1, 1]}/>
          <AnimatedMeshDistortMaterial distort={0.1} color='limegreen' transparent opacity={springsSide2.opacity}/>
        </mesh>
      </animated.group>
      <YootButtonModel
        position={layout[device].howToPlay.throwingTheDicePage.yootButtonModel.position}
        rotation={layout[device].howToPlay.throwingTheDicePage.yootButtonModel.rotation}
        turnedOn={yootButtonTurnedOn}
      />
      <Cursor
        position={layout[device].howToPlay.throwingTheDicePage.cursor.position}
        rotation={layout[device].howToPlay.throwingTheDicePage.cursor.rotation}
        scale={layout[device].howToPlay.throwingTheDicePage.cursor.scale}
      />
    </group>
  }

  function ScorePage() {

    const [CreateFirework] = useFireworksShader();

    useEffect(() => {
        let isVisible = !document.hidden;
        
        // Listen for visibility changes
        const handleVisibilityChange = () => {
            isVisible = !document.hidden;
        };

      document.addEventListener('visibilitychange', handleVisibilityChange)

      // When 'welcome home!' displays
      // anchor fireworks on 'mission accomplished' text
      let missionAccomplishedTextPosition = new THREE.Vector3()
      missionAccomplishedTextRef.current.getWorldPosition(missionAccomplishedTextPosition)
      const fireworkTimeout0 = setTimeout(() => {
        if (document.hidden) return
        // firework 1 - left
        const count = Math.round(500 + Math.random() * 400);
        let position, size, radius
        position = new THREE.Vector3(
          3 + missionAccomplishedTextPosition.x,
          2 + missionAccomplishedTextPosition.y,
          -1.5 + missionAccomplishedTextPosition.z
        )
        if (device === 'portrait') {
          size = 0.08 + Math.random() * 0.1
          radius = 0.9 + Math.random() * 0.1
        } else {
          size = 0.25 + Math.random() * 0.1
          radius = 0.6 + Math.random() * 0.1
        }
        const color = new THREE.Color();
        const hue = 0.01
        color.setHSL(hue, 0.7, 0.5)

        CreateFirework({ count, position, size, radius, color });
      }, 4500) 
      const fireworkTimeout1 = setTimeout(() => {
        if (document.hidden) return
        // firework 1 - left
        const count = Math.round(500 + Math.random() * 400);
        let position, size, radius
        position = new THREE.Vector3(
          1 + missionAccomplishedTextPosition.x,
          2 + missionAccomplishedTextPosition.y,
          -1.8 + missionAccomplishedTextPosition.z
        )
        if (device === 'portrait') {
          size = 0.08 + Math.random() * 0.1
          radius = 0.9 + Math.random() * 0.1
        } else {
          size = 0.25 + Math.random() * 0.1
          radius = 0.7 + Math.random() * 0.1
        }

        const color = new THREE.Color();
        const hue = 0.01
        color.setHSL(hue, 0.7, 0.5)
        
        CreateFirework({ count, position, size, radius, color });
      }, 4700) // When 'welcome home!' displays
      const fireworkTimeout2 = setTimeout(() => {
        if (document.hidden) return
        // firework 1 - left
        const count = Math.round(500 + Math.random() * 400);
        let position, size, radius
        position = new THREE.Vector3(
          -1 + missionAccomplishedTextPosition.x,
          2 + missionAccomplishedTextPosition.y,
          -1.6 + missionAccomplishedTextPosition.z
        )
        if (device === 'portrait') {
          size = 0.08 + Math.random() * 0.1
          radius = 0.9 + Math.random() * 0.1
        } else {
          size = 0.25 + Math.random() * 0.1
          radius = 0.65 + Math.random() * 0.1
        }

        const color = new THREE.Color();
        const hue = 0.01
        color.setHSL(hue, 0.7, 0.5)
        
        CreateFirework({ count, position, size, radius, color });
      }, 4900) // When 'welcome home!' displays
      const fireworkTimeout3 = setTimeout(() => {
        if (document.hidden) return
        // firework 1 - left
        const count = Math.round(500 + Math.random() * 400);
        let position, size, radius
        position = new THREE.Vector3(
          -3 + missionAccomplishedTextPosition.x,
          2 + missionAccomplishedTextPosition.y,
          -1.4 + missionAccomplishedTextPosition.z
        )
        if (device === 'portrait') {
          size = 0.08 + Math.random() * 0.1
          radius = 0.9 + Math.random() * 0.1
        } else {
          size = 0.25 + Math.random() * 0.1
          radius = 0.7 + Math.random() * 0.1
        }

        const color = new THREE.Color();
        const hue = 0.01
        color.setHSL(hue, 0.7, 0.5)
        
        CreateFirework({ count, position, size, radius, color });
      }, 5100) // When 'welcome home!' displays
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        clearTimeout(fireworkTimeout0)
        clearTimeout(fireworkTimeout1)
        clearTimeout(fireworkTimeout2)
        clearTimeout(fireworkTimeout3)
      }
    }, [])

    const TILE_RADIUS = 5
    const homeTokenShift = [-5, 0, 0]
    const springs = useSpring({
      from: {
        piggybackTokenPosition: [
          -Math.cos(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
          1.5,
          Math.sin(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS,
        ],
        movingTokenPosition: [
          -Math.cos(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
          1.5,
          Math.sin(((20) * (Math.PI * 2)) / 20) * TILE_RADIUS,
        ],
        movingTokenScale: 1.4,
        piggybackTokenScale: 1.4,
        welcomeHomeAlertScale: 0,
        scoredIndicator2Scale: 0,
        scoredIndicator3Scale: 0,
      },
      to: [
        {
          movingTokenPosition: [
            -Math.cos(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((21) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((22) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((23) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((24) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5,
            1.5,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
          piggybackTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5,
            1.5,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS,
          ],
        },
        {
          movingTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.8,
            2,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5,
          ],
          piggybackTokenPosition: [
            -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.8,
            2,
            Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5,
          ],
          movingTokenScale: 2.5,
          piggybackTokenScale: 2.5,
          scoredIndicator2Scale: 0.4,
          scoredIndicator3Scale: 0.4,
          welcomeHomeAlertScale: 1
        },
        // {
        //   movingTokenPosition: [
        //     -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 0.5 + homeTokenShift[0],
        //     1.5 + homeTokenShift[1],
        //     Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5 + homeTokenShift[2],
        //   ],
        //   piggybackTokenPosition: [
        //     -Math.cos(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS - 0.5 + homeTokenShift[0],
        //     1.5 + homeTokenShift[1],
        //     Math.sin(((25) * (Math.PI * 2)) / 20) * TILE_RADIUS + 1.5 + homeTokenShift[2],
        //   ],
        //   movingTokenScale: 0,
        //   piggybackTokenScale: 0,
        //   welcomeHomeAlertScale: 0,
        //   delay: 2200
        // },
      ],
      config: {
        tension: 170,
        friction: 26
      }
    })

    const missionAccomplishedTextRef = useRef();

    function WelcomeHomeAlert({ position, scale }) {
      
      const borderMesh0Ref = useRef();
      const borderMesh1Ref = useRef();
      const borderMesh2Ref = useRef();
      const borderMesh3Ref = useRef();
      const borderMesh4Ref = useRef();
      const borderMesh5Ref = useRef();
      const borderMesh6Ref = useRef();
      const borderMeshRefs = [
        borderMesh0Ref,
        borderMesh1Ref,
        borderMesh2Ref,
        borderMesh3Ref,
        borderMesh4Ref,
        borderMesh5Ref,
        borderMesh6Ref
      ]

      const height = 1.3
      const width = 2.4
      const starScale = 0.12
      useFrame((state) => {
        const time = state.clock.elapsedTime 
        for (let i = 0; i < borderMeshRefs.length; i++) {      
          if (borderMeshRefs[i].current) {
            borderMeshRefs[i].current.position.x = Math.cos(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * width
            borderMeshRefs[i].current.position.y = 0.05
            borderMeshRefs[i].current.position.z = Math.sin(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * height
          }
        }
      })

      return <animated.group position={position} scale={scale}>
        {/* <mesh scale={[width, 0.01, height]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={0.8}/>
        </mesh> */}
        <Text3D
        name='main-text'
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-2.5,0,-0.1]}
        rotation={layout[device].game.whoGoesFirst.title.rotation}
        size={0.6}
        height={layout[device].game.whoGoesFirst.title.height}
        lineHeight={0.8}
        ref={missionAccomplishedTextRef}>
          {translations.howToPlayHome.missionAccomplished[language]}
          <meshStandardMaterial color='red'/>
        </Text3D>
        {/* <group ref={borderMesh0Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group>
        <group ref={borderMesh1Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group>
        <group ref={borderMesh2Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group>
        <group ref={borderMesh3Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group>
        <group ref={borderMesh4Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group>
        <group ref={borderMesh5Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group>
        <group ref={borderMesh6Ref}>
          <Star 
            scale={starScale}
            color='red'
          />
        </group> */}
      </animated.group>
    }

    return <group name='score-page'>
      <group 
      name='board'
      position={layout[device].howToPlay.scoringPage.board.position}
      scale={layout[device].howToPlay.scoringPage.board.scale}>
        <Board
        showConstellations={false}
        omit
        showTiles={[0, 15, 16, 17, 18, 19, 29]}
        showArrows={false}
        />
        <animated.group name='piggyback-token' scale={springs.piggybackTokenScale} position={springs.piggybackTokenPosition}>
          <Rocket onBoard/>
        </animated.group>
        <animated.group name='moving -token' scale={springs.movingTokenScale} position={springs.movingTokenPosition}>
          <Rocket onBoard/>
        </animated.group>
      </group>
      <group name='rocket-home' 
      position={layout[device].howToPlay.scoringPage.rocketHome.position} 
      scale={layout[device].howToPlay.scoringPage.rocketHome.scale}>
        <mesh position={[0, -0.5, -0.2]}>
          <cylinderGeometry args={[1.4, 1.4, 0.01, 32]}/>
          <meshStandardMaterial color='red' transparent opacity={0.1}/>
        </mesh>
        <animated.group name='scored-indicator-0' position={[-0.5, 0, -0.5]} scale={0.4}>
          <Star color='red'/>
        </animated.group>
        <animated.group name='scored-indicator-1' position={[0.5, 0, -0.5]} scale={0.4}>
          <Star color='red'/>
        </animated.group>
        <animated.group name='scored-indicator-2' position={[-0.5, 0, 0.5]} scale={springs.scoredIndicator2Scale}>
          <Star color='red'/>
        </animated.group>
        <animated.group name='scored-indicator-3' position={[0.5, 0, 0.5]} scale={springs.scoredIndicator3Scale}>
          <Star color='red'/>
        </animated.group>
      </group>
      <WelcomeHomeAlert position={[4.8,1,-1]} scale={springs.welcomeHomeAlertScale}/>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={layout[device].howToPlay.scoringPage.text.position}
        rotation={layout[device].howToPlay.scoringPage.text.rotation}
        size={layout[device].howToPlay.scoringPage.text.size}
        height={layout[device].howToPlay.scoringPage.text.height}
        lineHeight={layout[device].howToPlay.scoringPage.text.lineHeight}
      >
        {translations.howToPlayHome.winningDescription[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
    </group>
  }

  function CatchEnemiesPage() {
    const springs = useSpring({
      from: {
        rocketScale: 1.5,
        legalTileScale: 0.4,
        pointerOpacity: 0,
        rocketPos: layout[device].howToPlay.catchingPiecesPage.rocketPos[0],
        ufoPos: layout[device].howToPlay.catchingPiecesPage.ufoPos[0],
        ufoScale: 1.5,
        moveTextScale: 1,
        bonusAlertScale: 0,
        yootButtonScale: 0,
        moveTokenScale: 1,
        moveToken1Scale: 0,
        catchTokenHomeScale: 0
      },
      to: [
        {
          moveToken1Scale: 1,
          rocketScale: 2.1,
          pointerOpacity: 1,
          ufoScale: 2,
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          delay: 200,
          config: {
            tension: 0,
          }
        },
        {
          rocketPos: layout[device].howToPlay.catchingPiecesPage.rocketPos[1],
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          rocketPos: layout[device].howToPlay.catchingPiecesPage.rocketPos[2],
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          rocketPos: layout[device].howToPlay.catchingPiecesPage.rocketPos[3],
          moveTokenScale: 0,
          moveToken1Scale: 0,
          legalTileScale: 0.4,
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          ufoPos: layout[device].howToPlay.catchingPiecesPage.ufoPos[1],
          ufoScale: 0,
          config: {
            tension: 100,
            friction: 26
          }
        },
        {
          catchTokenHomeScale: 1,
          moveTextScale: 0,
          bonusAlertScale: 1,
          yootButtonScale: layout[device].howToPlay.catchingPiecesPage.yootButtonModel.scale,
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          delay: 5000,
        },
      ],
      loop: true,
      delay: 500
    })

    function FirstCornerTiles({ position, scale }) {
      let tiles = [];

      //circle
      const NUM_STARS = 20
      const TILE_RADIUS = 5;
      for (let i = 0; i < 4; i++) {
        let position = [
          -Math.cos(((i+5) * (Math.PI * 2)) / NUM_STARS) * TILE_RADIUS,
          0,
          Math.sin(((i+5) * (Math.PI * 2)) / NUM_STARS) * TILE_RADIUS,
        ];
        if (i === 3) {
          tiles.push(
            <group 
              position={position}
              key={i}
            >
              <Star
                tile={i}
                scale={springs.legalTileScale}
                device={device}
              />
              <Pointer color='red' position={layout[device].howToPlay.catchingPiecesPage.pointer.position} scale={2} opacity={springs.pointerOpacity}/>
            </group>
          )
        } else {            
          tiles.push(
            <Star
              position={position}
              tile={i}
              key={i}
              scale={layout[device].howToPlay.star.scale}
              device={device}
            />
          )
        }
      }
  
      return <animated.group position={position} scale={scale}>
        { tiles }
        <animated.group name='rocket' position={springs.rocketPos} scale={springs.rocketScale} >
          <Rocket onBoard/>
        </animated.group>
        <animated.group name='ufo' position={springs.ufoPos} scale={springs.ufoScale} >
          <Ufo onBoard/>
        </animated.group>
          <GulToken position={[5,0,4]} rotation={[0, Math.PI/2, 0]} scale={springs.moveToken1Scale}/>
      </animated.group>;
    }

    function BonusAlert({ position, scale }) {
      
      const borderMesh0Ref = useRef();
      const borderMesh1Ref = useRef();
      const borderMesh2Ref = useRef();
      const borderMesh3Ref = useRef();
      const borderMesh4Ref = useRef();
      const borderMesh5Ref = useRef();
      const borderMesh6Ref = useRef();
      const borderMeshRefs = [
        borderMesh0Ref,
        borderMesh1Ref,
        borderMesh2Ref,
        borderMesh3Ref,
        borderMesh4Ref,
        borderMesh5Ref,
        borderMesh6Ref
      ]

      const height = 1.3
      const width = 1.9
      const starScale = 0.1
      const starColor = 'limegreen'
      useFrame((state) => {
        const time = state.clock.elapsedTime 
        for (let i = 0; i < borderMeshRefs.length; i++) {      
          if (borderMeshRefs[i].current) {
            borderMeshRefs[i].current.position.x = Math.cos(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * width
            borderMeshRefs[i].current.position.y = 0.05
            borderMeshRefs[i].current.position.z = Math.sin(time / 2 + 2 * Math.PI/borderMeshRefs.length * i) * height
          }
        }
      })

      return <animated.group position={position} scale={scale}>
        <mesh scale={[width, 0.01, height]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={0.3}/>
        </mesh>
        <Text3D
        name='main-text'
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-1.1,0,-0.15]}
        rotation={layout[device].game.whoGoesFirst.title.rotation}
        size={0.5}
        height={layout[device].game.whoGoesFirst.title.height}
        lineHeight={0.8}>
          {translations.howToPlayHome.bonusTurnAlert[language]}
          <meshStandardMaterial color={starColor}/>
        </Text3D>
        <group ref={borderMesh0Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
        <group ref={borderMesh1Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
        <group ref={borderMesh2Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
        <group ref={borderMesh3Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
        <group ref={borderMesh4Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
        <group ref={borderMesh5Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
        <group ref={borderMesh6Ref}>
          <Star 
            scale={starScale}
            color={starColor}
          />
        </group>
      </animated.group>
    }

    // ufo is flipped over, moved to a corner and scaled to 0. show sparkle
    return <group name='catch-enemies-page'>
      <FirstCornerTiles 
      position={layout[device].howToPlay.catchingPiecesPage.firstCornerTiles.position} 
      scale={layout[device].howToPlay.catchingPiecesPage.firstCornerTiles.scale}/>
      <group name='ufo-home' 
      position={layout[device].howToPlay.catchingPiecesPage.ufoHome.position} 
      scale={layout[device].howToPlay.catchingPiecesPage.ufoHome.scale}>
        <mesh name='base' position={[0, -0.5, -0.2]}>
          <cylinderGeometry args={[1.4, 1.4, 0.01, 32]}/>
          <meshStandardMaterial color='turquoise' transparent opacity={0.05}/>
        </mesh>
        <Ufo position={[-0.5,0,-0.4]}/>
        <Ufo position={[0.5,0,-0.4]}/>
        <Ufo position={[-0.5,0,0.4]}/>
        <animated.group scale={springs.catchTokenHomeScale}>
          <Ufo position={[0.5,0,0.4]}/>
        </animated.group>
      </group>
      <YootButtonModel 
      scale={springs.yootButtonScale} 
      rotation={[Math.PI/16, Math.PI/2, 0]} 
      position={layout[device].howToPlay.catchingPiecesPage.yootButtonModel.position} 
      turnedOn={true}/>
      <BonusAlert 
      position={layout[device].howToPlay.catchingPiecesPage.bonusAlert.position} 
      scale={springs.bonusAlertScale}/>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={layout[device].howToPlay.catchingPiecesPage.text.position}
        rotation={layout[device].howToPlay.catchingPiecesPage.text.rotation}
        size={layout[device].howToPlay.catchingPiecesPage.text.size}
        height={layout[device].howToPlay.catchingPiecesPage.text.height}
        lineHeight={layout[device].howToPlay.catchingPiecesPage.text.lineHeight}
      >
        {translations.howToPlayHome.capturingDescriptionFull[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
    </group>
  }

  function PiggybackPage() {    
    const springs = useSpring({
      from: {
        rocket0Scale: 1.2,
        rocket1Scale: 1.2,
        legalTile0Scale: 0.4,
        legalTile1Scale: 0.4,
        pointer0Opacity: 0,
        pointer1Opacity: 0,
        rocket0Pos: layout[device].howToPlay.combiningPiecesPage.rocket0Pos[0],
        rocket1Pos: layout[device].howToPlay.combiningPiecesPage.rocket1Pos[0],
        moveText0Scale: 1,
        moveText1Scale: 0,
        yootButtonScale: 0,
        moveTokenScale: 0,
        moveToken1Scale: 0,
        gulTokenScale: 1,
        geTokenScale: 1
      },
      to: [
        {
          rocket0Scale: 1.6,
          rocket1Scale: 1.6,
          legalTile0Scale: 0.6,
          pointer0Opacity: 1,
          moveTokenScale: 1,
        },
        {
          delay: 200,
          config: {
            tension: 0,
          }
        },
        {
          rocket0Pos: layout[device].howToPlay.combiningPiecesPage.rocket0Pos[1],
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          rocket0Pos: layout[device].howToPlay.combiningPiecesPage.rocket0Pos[2],
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          rocket0Pos: layout[device].howToPlay.combiningPiecesPage.rocket0Pos[3],
          rocket1Pos: layout[device].howToPlay.combiningPiecesPage.rocket1Pos[1],
          legalTile0Scale: 0.4,
          gulTokenScale: 0,
          moveTokenScale: 0,
          moveToken1Scale: 1,
          legalTile1Scale: 0.7,
          pointer1Opacity: 1,
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          rocket0Pos: layout[device].howToPlay.combiningPiecesPage.rocket0Pos[4],
          rocket1Pos: layout[device].howToPlay.combiningPiecesPage.rocket1Pos[2],
          delay: 200,
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          rocket0Pos: layout[device].howToPlay.combiningPiecesPage.rocket0Pos[5],
          rocket1Pos: layout[device].howToPlay.combiningPiecesPage.rocket1Pos[3],
          moveToken1Scale: 0,
          legalTile1Scale: 0.4,
          geTokenScale: 0,
          config: {
            tension: 170,
            friction: 26
          }
        },
        {
          delay: 5000,
        },
      ],
      loop: true,
      delay: 500
    })

    function FirstCornerTiles({ position, scale }) {
      let tiles = [];

      //circle
      const NUM_STARS = 20
      const TILE_RADIUS = 5;
      for (let i = -1; i < 5; i++) {
        let position = [
          -Math.cos(((i+5) * (Math.PI * 2)) / NUM_STARS) * TILE_RADIUS,
          0,
          Math.sin(((i+5) * (Math.PI * 2)) / NUM_STARS) * TILE_RADIUS,
        ];
        if (i === 2) {
          tiles.push(
            <group 
              position={position}
              key={i}
            >
              <Star scale={springs.legalTile0Scale}/>
            </group>
          )
        } else if (i === 4) {
          tiles.push(
            <group
              position={position}
              key={i}
            >
              <Mars
                scale={springs.legalTile1Scale}
              />
            </group>
          )
        } else {            
          tiles.push(
            <Star
              position={position}
              scale={layout[device].howToPlay.star.scale}
              key={i}
            />
          )
        }
      }
  
      return <group position={position} scale={scale}>
        { tiles }
        <animated.group name='rocket-0' position={springs.rocket0Pos} scale={springs.rocket0Scale} >
          <Rocket onBoard/>
        </animated.group>
        <animated.group name='rocket-1' position={springs.rocket1Pos} scale={springs.rocket1Scale} >
          <Rocket onBoard/>
        </animated.group>
        <animated.group>
          <GulToken scale={springs.moveTokenScale} position={[4,0,5]} rotation={[0, Math.PI/2, 0]}/>
        </animated.group>
        <animated.group >
          <GeToken scale={springs.moveToken1Scale} position={[6,0,0]} rotation={[0, Math.PI/2, 0]}/>
        </animated.group>
      </group>;
    }

    return <group>
      <FirstCornerTiles 
      position={layout[device].howToPlay.combiningPiecesPage.firstCornerTiles.position}
      scale={1.3}/>
      <animated.group>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"} 
          position={layout[device].howToPlay.combiningPiecesPage.moveText0.position}
          rotation={layout[device].howToPlay.combiningPiecesPage.moveText0.rotation}
          size={layout[device].howToPlay.combiningPiecesPage.moveText0.size} 
          height={layout[device].howToPlay.combiningPiecesPage.moveText0.height}
          lineHeight={layout[device].howToPlay.combiningPiecesPage.moveText0.lineHeight}
        >
          {translations.howToPlayHome.moves[language]}
          <meshStandardMaterial color={ "limegreen" }/>
        </Text3D>
        <GeToken 
          position={layout[device].howToPlay.combiningPiecesPage.geToken0.position} 
          rotation={layout[device].howToPlay.combiningPiecesPage.geToken0.rotation}
          scale={springs.geTokenScale}
        />
        <GulToken 
          position={layout[device].howToPlay.combiningPiecesPage.gulToken.position} 
          rotation={layout[device].howToPlay.combiningPiecesPage.gulToken.rotation}
          scale={springs.gulTokenScale}
        />
      </animated.group>
      <animated.group scale={springs.moveText1Scale}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"} 
          position={layout[device].howToPlay.combiningPiecesPage.moveText1.position}
          rotation={layout[device].howToPlay.combiningPiecesPage.moveText1.rotation}
          size={layout[device].howToPlay.combiningPiecesPage.moveText1.size} 
          height={layout[device].howToPlay.combiningPiecesPage.moveText1.height}
          lineHeight={layout[device].howToPlay.combiningPiecesPage.moveText1.lineHeight}
        >
          {translations.howToPlayHome.moves[language]}
          <meshStandardMaterial color={ "limegreen" }/>
        </Text3D>
        <GeToken 
          position={layout[device].howToPlay.combiningPiecesPage.geToken1.position} 
          rotation={layout[device].howToPlay.combiningPiecesPage.geToken1.rotation}
        />
      </animated.group>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={layout[device].howToPlay.combiningPiecesPage.text.position}
        rotation={layout[device].howToPlay.combiningPiecesPage.text.rotation}
        size={layout[device].howToPlay.combiningPiecesPage.text.size}
        height={layout[device].howToPlay.combiningPiecesPage.text.height}
        lineHeight={layout[device].howToPlay.combiningPiecesPage.text.lineHeight}
      >
        {translations.howToPlayHome.stackingDescription[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
    </group>
  }
  
  function MovementPage() {
    // #region Springs
    const arrowFriction = 10
    const arrowTension = 180
    const arrowDelay = 100
    const springsArrow0 = useSpring({
      from: {
        scale: 0
      },
      to: [
        {
          scale: 1.5,
          config: {
            tension: arrowTension,
            friction: arrowFriction
          },
          delay: arrowDelay
        }
      ],
    })
    const springsArrow1 = useSpring({
      from: {
        scale: 0
      },
      to: [
        {
          scale: 1.5,
          config: {
            tension: arrowTension,
            friction: arrowFriction
          },
          delay: arrowDelay + 100
        }
      ],
    })
    const springsArrow2 = useSpring({
      from: {
        scale: 0
      },
      to: [
        {
          scale: 1.5,
          config: {
            tension: arrowTension,
            friction: arrowFriction
          },
          delay: arrowDelay + 200
        }
      ],
    })
    const springsArrow3 = useSpring({
      from: {
        scale: 0
      },
      to: [
        {
          scale: 1.5,
          config: {
            tension: arrowTension,
            friction: arrowFriction
          },
          delay: arrowDelay + 300
        }
      ],
    })
    const life = 300
    const startDelay = 100
    const frictionWobbly = 15
    const delay = 50
    // Path 0
    const dotSpringsCircle = []
    const numDotSpringsCircle = 70
    for (let i = 0; i < numDotSpringsCircle; i++) {
      dotSpringsCircle.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    // Path 1
    const dotSpringsFirstCorner = []
    const numDotSpringsFirstCorner = 16
    for (let i = 0; i < numDotSpringsFirstCorner; i++) {
      dotSpringsFirstCorner.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + delay * numDotSpringsCircle + i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    const dotSpringsMiddle = []
    const numDotSpringsMiddle = 24
    for (let i = 0; i < numDotSpringsMiddle; i++) {
      dotSpringsMiddle.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + delay * numDotSpringsCircle + delay * numDotSpringsFirstCorner + i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    const dotSpringsCorner = []
    const numDotSpringsCorner = 16
    for (let i = 0; i < numDotSpringsCorner; i++) {
      dotSpringsCorner.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + delay * numDotSpringsCircle + delay * numDotSpringsFirstCorner + delay * numDotSpringsMiddle + i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    // Path 2
    const dotSpringsHalfCircle = []
    const numDotSpringsHalfCircle = 35
    for (let i = 0; i < numDotSpringsHalfCircle; i++) {
      dotSpringsHalfCircle.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + 
            delay * numDotSpringsCircle + 
            delay * numDotSpringsFirstCorner + 
            delay * numDotSpringsMiddle + 
            delay * numDotSpringsCorner + i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    const dotSpringsVertical = []
    const numDotSpringsVertical = 25
    for (let i = 0; i < numDotSpringsVertical; i++) {
      dotSpringsVertical.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + 
            delay * numDotSpringsCircle + 
            delay * numDotSpringsFirstCorner + 
            delay * numDotSpringsMiddle + 
            delay * numDotSpringsCorner + 
            delay * numDotSpringsHalfCircle + 
            i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    // Path 3
    const dotSpringsFirstCornerPath3 = []
    const numDotSpringsFirstCornerPath3 = 16
    for (let i = 0; i < numDotSpringsFirstCornerPath3; i++) {
      dotSpringsFirstCornerPath3.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + 
            delay * numDotSpringsCircle + 
            delay * numDotSpringsFirstCorner + 
            delay * numDotSpringsMiddle + 
            delay * numDotSpringsCorner + 
            delay * numDotSpringsHalfCircle + 
            delay * numDotSpringsVertical + 
            i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    const dotSpringsMiddleHalf = []
    const numDotSpringsMiddleHalf = 10
    for (let i = 0; i < numDotSpringsMiddleHalf; i++) {
      dotSpringsMiddleHalf.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            delay: startDelay + 
            delay * numDotSpringsCircle +
            delay * numDotSpringsFirstCorner + 
            delay * numDotSpringsMiddle + 
            delay * numDotSpringsCorner + 
            delay * numDotSpringsHalfCircle + 
            delay * numDotSpringsVertical + 
            delay * numDotSpringsFirstCornerPath3 + 
            i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    const dotSpringsVerticalHalf = []
    const numDotSpringsVerticalHalf = 10
    for (let i = 0; i < numDotSpringsVerticalHalf; i++) {
      dotSpringsVerticalHalf.push(useSpring({
        from: {
          scale: 0
        },
        to: [
          {
            scale: 0.1,
            config: {
              tension: 180,
              friction: frictionWobbly
            },
            // Extra delay to highlight moving from the Moon
            delay: startDelay + 
            delay * numDotSpringsCircle +
            delay * numDotSpringsFirstCorner + 
            delay * numDotSpringsMiddle + 
            delay * numDotSpringsCorner + 
            delay * numDotSpringsHalfCircle + 
            delay * numDotSpringsVertical + 
            delay * numDotSpringsFirstCornerPath3 + 
            delay * numDotSpringsMiddleHalf +
            i * delay
          },
          {
            scale: 0,
            config: {
              tension: 170,
              friction: 26
            },
            delay: life
          }
        ],
      }))
    }
    // #endregion

    return <group name='movement-page'>
      <animated.group name='text'>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.movementPage.text.position} 
          rotation={layout[device].howToPlay.movementPage.text.rotation}
          size={layout[device].howToPlay.movementPage.text.size}
          height={layout[device].howToPlay.movementPage.text.height}
          lineHeight={layout[device].howToPlay.movementPage.text.lineHeight}
        >
          {translations.howToPlayHome.movementDescriptionFull[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>
      </animated.group>
      <group 
      position={layout[device].howToPlay.movementPage.board.position} 
      scale={layout[device].howToPlay.movementPage.board.scale}>
        <Board showArrows={false} highlightShortcuts/>
        <group name='path-0'>
          {dotSpringsCircle.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[5 * Math.cos(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32)), 0.5, 0.2 - 5 * Math.sin(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32))]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='limegreen'/>
            </animated.mesh>
          })}
        </group>
        <group name='path-1'>
          {dotSpringsFirstCorner.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[5 * Math.cos(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32)), 0.5, 0.2 - 5 * Math.sin(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32))]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='#00f7ff'/>
            </animated.mesh>
          })}
          {dotSpringsMiddle.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[4.2 - 0.4*index, 0.5, 0.2]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='#00f7ff'/>
            </animated.mesh>
          })}
          {dotSpringsCorner.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[5 * Math.cos(Math.PI + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32)), 0.5, 0.2 - 5 * Math.sin(Math.PI + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32))]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='#00f7ff'/>
            </animated.mesh>
          })}
        </group>
        <group name='path-2'>
          {dotSpringsHalfCircle.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[5 * Math.cos(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32)), 0.5, 0.2 - 5 * Math.sin(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32))]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='limegreen'/>
            </animated.mesh>
          })}
          {dotSpringsVertical.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[0, 0.5, -4.5 + 0.4*index]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='limegreen'/>
            </animated.mesh>
          })}
        </group>
        <group name='path-3'>
          {dotSpringsFirstCornerPath3.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[5 * Math.cos(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32)), 0.5, 0.2 - 5 * Math.sin(-Math.PI/2 + ((index) / dotSpringsCorner.length) * (Math.PI/2 - Math.PI/32))]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='#00f7ff'/>
            </animated.mesh>
          })}
          {dotSpringsMiddleHalf.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[4.2 - 0.4*index, 0.5, 0.2]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='#00f7ff'/>
            </animated.mesh>
          })}
          {dotSpringsVerticalHalf.map((value, index) => {
            return <animated.mesh key={index} scale={value.scale} position={[0, 0.5, 0.7 + 0.4*index]}>
              <sphereGeometry args={[1, 32, 16]}/>
              <meshBasicMaterial color='#00f7ff'/>
            </animated.mesh>
          })}
        </group>
        {/* <group name='mars-left-arrow' position={[-0.4, 1.0, 0.2]}>
          <animated.mesh scale={springsArrow0.scale} position={[4, 0, 0]} rotation={[Math.PI/2, 0, Math.PI/2]}>
            <coneGeometry args={[0.15, 0.4, 32]}/>
            <meshBasicMaterial color='#33ff00'/>
          </animated.mesh>
        </group>
        <group name='moon-left-arrow' position={[-5.45, 1.0, 0.2]}>
          <animated.mesh scale={springsArrow1.scale} position={[4, 0, 0]} rotation={[Math.PI/2, 0, Math.PI/2]}>
            <coneGeometry args={[0.15, 0.4, 32]}/>
            <meshBasicMaterial color='#33ff00'/>
          </animated.mesh>
        </group>
        <group name='saturn-bottom-arrow' position={[-4, 1.0, -3.3]}>
          <animated.mesh scale={springsArrow2.scale} position={[4, 0, 0]} rotation={[Math.PI/2, 0, 0]}>
            <coneGeometry args={[0.15, 0.4, 32]}/>
            <meshBasicMaterial color='#33ff00'/>
          </animated.mesh>
        </group>
        <group name='moon-bottom-arrow' position={[-4, 1.0, 1.8]}>
          <animated.mesh scale={springsArrow3.scale} position={[4, 0, 0]} rotation={[Math.PI/2, 0, 0]}>
            <coneGeometry args={[0.15, 0.4, 32]}/>
            <meshBasicMaterial color='#33ff00'/>
          </animated.mesh>
        </group> */}
      </group>
    </group>
  }

  function ReadTheYutPage() {
    return <group>
      <group name='text'>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.text.position}
          rotation={layout[device].howToPlay.readingTheDicePage.text.rotation}
          size={layout[device].howToPlay.readingTheDicePage.text.size}
          height={layout[device].howToPlay.readingTheDicePage.text.height}
        >
          {`${translations.howToPlayHome.eachFlatSide[language]}\n${translations.howToPlayHome.yutOrMoBonusFull[language]}`}
          <meshStandardMaterial color='orange'/>
        </Text3D>
      </group>
      <group 
      position={layout[device].howToPlay.readingTheDicePage.do.position} 
      scale={layout[device].howToPlay.readingTheDicePage.do.scale}>     
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.do.text.line0.position}
          rotation={layout[device].howToPlay.readingTheDicePage.do.text.line0.rotation}
          size={layout[device].howToPlay.readingTheDicePage.do.text.line0.size}
          height={layout[device].howToPlay.readingTheDicePage.do.text.line0.height}
        >
          {translations.howToPlayHome.do[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.do.text.line1.position}
          rotation={layout[device].howToPlay.readingTheDicePage.do.text.line1.rotation}
          size={layout[device].howToPlay.readingTheDicePage.do.text.line1.size}
          height={layout[device].howToPlay.readingTheDicePage.do.text.line1.height}
        >
          {translations.howToPlayHome.oneStickFlat[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.do.text.line2.position}
          rotation={layout[device].howToPlay.readingTheDicePage.do.text.line2.rotation}
          size={layout[device].howToPlay.readingTheDicePage.do.text.line2.size}
          height={layout[device].howToPlay.readingTheDicePage.do.text.line2.height}
        >
          {translations.howToPlayHome.oneSpace[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>   
        <Float rotationIntensity={0.1}>
          <YootSet 
          points="do" 
          scale={layout[device].howToPlay.readingTheDicePage.do.yootSet.scale} 
          position={layout[device].howToPlay.readingTheDicePage.do.yootSet.position}/>
        </Float>
      </group>
      <group 
      position={layout[device].howToPlay.readingTheDicePage.ge.position} 
      scale={layout[device].howToPlay.readingTheDicePage.ge.scale}>        
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.ge.text.line0.position}
          rotation={layout[device].howToPlay.readingTheDicePage.ge.text.line0.rotation}
          size={layout[device].howToPlay.readingTheDicePage.ge.text.line0.size}
          height={layout[device].howToPlay.readingTheDicePage.ge.text.line0.height}
        >
          {translations.howToPlayHome.ge[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.ge.text.line1.position}
          rotation={layout[device].howToPlay.readingTheDicePage.ge.text.line1.rotation}
          size={layout[device].howToPlay.readingTheDicePage.ge.text.line1.size}
          height={layout[device].howToPlay.readingTheDicePage.ge.text.line1.height}
        >
          {translations.howToPlayHome.twoFlat[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.ge.text.line2.position}
          rotation={layout[device].howToPlay.readingTheDicePage.ge.text.line2.rotation}
          size={layout[device].howToPlay.readingTheDicePage.ge.text.line2.size}
          height={layout[device].howToPlay.readingTheDicePage.ge.text.line2.height}
        >
          {translations.howToPlayHome.twoSpaces[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>   
        <Float rotationIntensity={0.1}>
          <YootSet 
          points="ge" 
          scale={layout[device].howToPlay.readingTheDicePage.ge.yootSet.scale} 
          position={layout[device].howToPlay.readingTheDicePage.ge.yootSet.position}/>
        </Float>
      </group>
      <group 
      position={layout[device].howToPlay.readingTheDicePage.gul.position} 
      scale={layout[device].howToPlay.readingTheDicePage.gul.scale}>        
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.gul.text.line0.position}
          rotation={layout[device].howToPlay.readingTheDicePage.gul.text.line0.rotation}
          size={layout[device].howToPlay.readingTheDicePage.gul.text.line0.size}
          height={layout[device].howToPlay.readingTheDicePage.gul.text.line0.height}
        >
          {translations.howToPlayHome.gul[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.gul.text.line1.position}
          rotation={layout[device].howToPlay.readingTheDicePage.gul.text.line1.rotation}
          size={layout[device].howToPlay.readingTheDicePage.gul.text.line1.size}
          height={layout[device].howToPlay.readingTheDicePage.gul.text.line1.height}
        >
          {translations.howToPlayHome.threeFlat[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>   
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.gul.text.line2.position}
          rotation={layout[device].howToPlay.readingTheDicePage.gul.text.line2.rotation}
          size={layout[device].howToPlay.readingTheDicePage.gul.text.line2.size}
          height={layout[device].howToPlay.readingTheDicePage.gul.text.line2.height}
        >
          {translations.howToPlayHome.threeSpaces[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>   
        <Float rotationIntensity={0.1}>
        <YootSet 
        points="gul" 
        scale={layout[device].howToPlay.readingTheDicePage.gul.yootSet.scale} 
        position={layout[device].howToPlay.readingTheDicePage.gul.yootSet.position}/>
        </Float>
      </group>
      <group
      position={layout[device].howToPlay.readingTheDicePage.yoot.position} 
      scale={layout[device].howToPlay.readingTheDicePage.yoot.scale}>            
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.yoot.text.line0.position}
          rotation={layout[device].howToPlay.readingTheDicePage.yoot.text.line0.rotation}
          size={layout[device].howToPlay.readingTheDicePage.yoot.text.line0.size}
          height={layout[device].howToPlay.readingTheDicePage.yoot.text.line0.height}
        >
          {`${translations.howToPlayHome.yut[language]}: ${translations.howToPlayHome.allFlat[language]}`}
          <meshStandardMaterial color='orange'/>
        </Text3D>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.yoot.text.line1.position}
          rotation={layout[device].howToPlay.readingTheDicePage.yoot.text.line1.rotation}
          size={layout[device].howToPlay.readingTheDicePage.yoot.text.line1.size}
          height={layout[device].howToPlay.readingTheDicePage.yoot.text.line1.height}
        >
          {translations.howToPlayHome.fourSpaces[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>   
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.yoot.text.line2.position}
          rotation={layout[device].howToPlay.readingTheDicePage.yoot.text.line2.rotation}
          size={layout[device].howToPlay.readingTheDicePage.yoot.text.line2.size}
          height={layout[device].howToPlay.readingTheDicePage.yoot.text.line2.height}
        >
          {translations.howToPlayHome.bonusTurn[language]}
          <meshStandardMaterial color='limegreen'/>
        </Text3D>   
        <Float rotationIntensity={0.1}>
          <YootSet 
          points="yoot" 
          scale={layout[device].howToPlay.readingTheDicePage.yoot.yootSet.scale} 
          position={layout[device].howToPlay.readingTheDicePage.yoot.yootSet.position}/>
        </Float>
      </group>
      <group       
      position={layout[device].howToPlay.readingTheDicePage.mo.position} 
      scale={layout[device].howToPlay.readingTheDicePage.mo.scale}>   
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.mo.text.line0.position}
          rotation={layout[device].howToPlay.readingTheDicePage.mo.text.line0.rotation}
          size={layout[device].howToPlay.readingTheDicePage.mo.text.line0.size}
          height={layout[device].howToPlay.readingTheDicePage.mo.text.line0.height}
        >
          {`${translations.howToPlayHome.mo[language]}: ${translations.howToPlayHome.noFlat[language]}`}
          <meshStandardMaterial color='orange'/>
        </Text3D>      
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.mo.text.line1.position}
          rotation={layout[device].howToPlay.readingTheDicePage.mo.text.line1.rotation}
          size={layout[device].howToPlay.readingTheDicePage.mo.text.line1.size}
          height={layout[device].howToPlay.readingTheDicePage.mo.text.line1.height}
        >
          {translations.howToPlayHome.fiveSpaces[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>      
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.mo.text.line2.position}
          rotation={layout[device].howToPlay.readingTheDicePage.mo.text.line2.rotation}
          size={layout[device].howToPlay.readingTheDicePage.mo.text.line2.size}
          height={layout[device].howToPlay.readingTheDicePage.mo.text.line2.height}
        >
          {translations.howToPlayHome.bonusTurn[language]}
          <meshStandardMaterial color='limegreen'/>
        </Text3D>      
        <Float rotationIntensity={0.1}>
        <YootSet 
        points="mo" 
        scale={layout[device].howToPlay.readingTheDicePage.mo.yootSet.scale} 
        position={layout[device].howToPlay.readingTheDicePage.mo.yootSet.position}/>
        </Float>
      </group>
      <group 
      position={layout[device].howToPlay.readingTheDicePage.backdo.position} 
      scale={layout[device].howToPlay.readingTheDicePage.backdo.scale}>       
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.backdo.text.line0.position}
          rotation={layout[device].howToPlay.readingTheDicePage.backdo.text.line0.rotation}
          size={layout[device].howToPlay.readingTheDicePage.backdo.text.line0.size}
          height={layout[device].howToPlay.readingTheDicePage.backdo.text.line0.height}
        >
          {translations.howToPlayHome.backdo[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>         
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.backdo.text.line1.position}
          rotation={layout[device].howToPlay.readingTheDicePage.backdo.text.line1.rotation}
          size={layout[device].howToPlay.readingTheDicePage.backdo.text.line1.size}
          height={layout[device].howToPlay.readingTheDicePage.backdo.text.line1.height}
        >
          {translations.howToPlayHome.minusOneSpace[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>      
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].howToPlay.readingTheDicePage.backdo.text.line2.position}
          rotation={layout[device].howToPlay.readingTheDicePage.backdo.text.line2.rotation}
          size={layout[device].howToPlay.readingTheDicePage.backdo.text.line2.size}
          height={layout[device].howToPlay.readingTheDicePage.backdo.text.line2.height}
        >
          {translations.howToPlayHome.backward[language]}
          <meshStandardMaterial color='red'/>
        </Text3D>
        <Float rotationIntensity={0.1}>
          <YootSet 
          points="backdo" 
          scale={layout[device].howToPlay.readingTheDicePage.backdo.yootSet.scale} 
          position={layout[device].howToPlay.readingTheDicePage.backdo.yootSet.position}/>
        </Float>
      </group>
    </group>
  }

  function Tabs({ position=[0,0,0], scale=1, orientation='bottom' }) {
    const [overviewHover, setOverviewHover] = useState(false)
    const [throwTheYutHover, setThrowTheYutHover] = useState(false)
    const [catchEnemiesHover, setCatchEnemiesHover] = useState(false)
    const [piggybackHover, setPiggybackHover] = useState(false)
    const [scoreHover, setScoreHover] = useState(false)
    const [readTheDiceHover, setReadTheDiceHover] = useState(false)
    const [shortcutHover, setShortcutHover] = useState(false)
    const [tipsHover, setTipsHover] = useState(false)

    function handleOverviewClick() {
      setPage(0)
      setOverviewHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handleOverviewPointerEnter() {
      document.body.style.cursor = 'pointer'
      setOverviewHover(true)
    }
    function handleOverviewPointerLeave() {
      document.body.style.cursor = 'default'
      setOverviewHover(false)
    }
    function handleThrowTheYutClick() {
      setPage(1)
      setThrowTheYutHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handleThrowTheYutPointerEnter() {
      document.body.style.cursor = 'pointer'
      setThrowTheYutHover(true)
    }
    function handleThrowTheYutPointerLeave() {
      document.body.style.cursor = 'default'
      setThrowTheYutHover(false)
    }
    function handleReadTheDiceClick() {
      setPage(2)
      setReadTheDiceHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handleReadTheDicePointerEnter() {
      document.body.style.cursor = 'pointer'
      setReadTheDiceHover(true)
    }
    function handleReadTheDicePointerLeave() {
      document.body.style.cursor = 'default'
      setReadTheDiceHover(false)
    }
    function handleShortcutClick() {
      setPage(3)
      setShortcutHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handleShortcutPointerEnter() {
      document.body.style.cursor = 'pointer'
      setShortcutHover(true)
    }
    function handleShortcutPointerLeave() {
      document.body.style.cursor = 'default'
      setShortcutHover(false)
    }
    function handleCatchEnemiesClick() {
      setPage(4)
      setCatchEnemiesHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handleCatchEnemiesPointerEnter() {
      document.body.style.cursor = 'pointer'
      setCatchEnemiesHover(true)
    }
    function handleCatchEnemiesPointerLeave() {
      document.body.style.cursor = 'default'
      setCatchEnemiesHover(false)
    }
    function handlePiggybackClick() {
      setPage(5)
      setPiggybackHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handlePiggybackPointerEnter() {
      document.body.style.cursor = 'pointer'
      setPiggybackHover(true)
    }
    function handlePiggybackPointerLeave() {
      document.body.style.cursor = 'default'
      setPiggybackHover(false)
    }
    function handleScoreClick() {
      setPage(6)
      setScoreHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }
    function handleScorePointerEnter() {
      document.body.style.cursor = 'pointer'
      setScoreHover(true)
    }
    function handleScorePointerLeave() {
      document.body.style.cursor = 'default'
      setScoreHover(false)
    }
    function handleTipsPointerEnter() {
      document.body.style.cursor = 'pointer'
      setTipsHover(true)
    }
    function handleTipsPointerLeave() {
      document.body.style.cursor = 'default'
      setTipsHover(false)
    }
    function handleTipsClick() {
      setPage(7)
      setTipsHover(false)
      setTabClicked(true)
      clearTimeout(pageTimeoutRef.current)
    }

    if (orientation === 'bottom') {
      return <group name='tabs' position={position} scale={scale}>
        <group name='tab-0' position={[0,0,0]} scale={0.8}>
          <mesh position={[0.9, -0.1, -0.2]}>
            <boxGeometry args={[2.2, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[0.9, -0.1, -0.2]}>
            <boxGeometry args={[2.3, 0.04, 0.85]}/>
            <meshStandardMaterial color={overviewHover || page === 0 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-0-wrapper' 
            position={[0.9, -0.1, -0.2]}
            onClick={handleOverviewClick}
            onPointerEnter={handleOverviewPointerEnter}
            onPointerLeave={handleOverviewPointerLeave}
          >
            <boxGeometry args={[2.3, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[0.9, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab1Goal[language]}
              <meshStandardMaterial color={overviewHover || page === 0 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-1' position={[1.9,0,0]} scale={0.8}>
          <mesh position={[2.3, -0.1, -0.2]}>
            <boxGeometry args={[4.9, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[2.3, -0.1, -0.2]}>
            <boxGeometry args={[5, 0.04, 0.85]}/>
            <meshStandardMaterial color={throwTheYutHover || page === 1 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-1-wrapper' 
            position={[2.3, -0.1, -0.2]}
            onClick={handleThrowTheYutClick}
            onPointerEnter={handleThrowTheYutPointerEnter}
            onPointerLeave={handleThrowTheYutPointerLeave}
          >
            <boxGeometry args={[5, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[2.3, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab2ThrowTheYut[language]}
              <meshStandardMaterial color={throwTheYutHover || page === 1 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-2' position={[6,0,0]} scale={0.8}>
          <mesh position={[2.05, -0.1, -0.2]}>
            <boxGeometry args={[4.4, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[2.05, -0.1, -0.2]}>
            <boxGeometry args={[4.5, 0.04, 0.85]}/>
            <meshStandardMaterial color={readTheDiceHover || page === 2 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-2-wrapper' 
            position={[2.05, -0.1, -0.2]}
            onClick={handleReadTheDiceClick}
            onPointerEnter={handleReadTheDicePointerEnter}
            onPointerLeave={handleReadTheDicePointerLeave}
          >
            <boxGeometry args={[4.5, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[2.05, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab3ReadTheYut[language]}
              <meshStandardMaterial color={readTheDiceHover || page === 2 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-3' position={[0,0,0.8]} scale={0.8}>
          <mesh position={[1.65, -0.1, -0.2]}>
            <boxGeometry args={[3.7, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[1.65, -0.1, -0.2]}>
            <boxGeometry args={[3.8, 0.04, 0.85]}/>
            <meshStandardMaterial color={shortcutHover || page === 3 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-3-wrapper' 
            position={[1.65, -0.1, -0.2]}
            onClick={handleShortcutClick}
            onPointerEnter={handleShortcutPointerEnter}
            onPointerLeave={handleShortcutPointerLeave}
          >
            <boxGeometry args={[3.8, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[1.65, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab4Movement[language]}
              <meshStandardMaterial color={shortcutHover || page === 3 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-4' position={[3.1, 0, 0.8]} scale={0.8}>
          <mesh position={[2.15, -0.1, -0.2]}>
            <boxGeometry args={[4.6, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[2.15, -0.1, -0.2]}>
            <boxGeometry args={[4.7, 0.04, 0.85]}/>
            <meshStandardMaterial color={catchEnemiesHover || page === 4 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-4-wrapper' 
            position={[2.15, -0.1, -0.2]}
            onClick={handleCatchEnemiesClick}
            onPointerEnter={handleCatchEnemiesPointerEnter}
            onPointerLeave={handleCatchEnemiesPointerLeave}
          >
            <boxGeometry args={[4.7, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[2.15, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab5CatchEnemies[language]}
              <meshStandardMaterial color={catchEnemiesHover || page === 4 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-5' position={[6.95,0,0.8]} scale={0.8}>
          <mesh position={[1.75, -0.1, -0.2]}>
            <boxGeometry args={[3.8, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[1.75, -0.1, -0.2]}>
            <boxGeometry args={[3.9, 0.04, 0.85]}/>
            <meshStandardMaterial color={piggybackHover || page === 5 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-5-wrapper' 
            position={[1.75, -0.1, -0.2]}
            onClick={handlePiggybackClick}
            onPointerEnter={handlePiggybackPointerEnter}
            onPointerLeave={handlePiggybackPointerLeave}
          >
            <boxGeometry args={[3.8, 0.1, 0.7]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[1.75, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab6Piggyback[language]}
              <meshStandardMaterial color={piggybackHover || page === 5 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-6' position={[0,0,1.6]} scale={0.8}>
          <mesh position={[1.45, -0.1, -0.2]}>
            <boxGeometry args={[3.25, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[1.45, -0.1, -0.2]}>
            <boxGeometry args={[3.35, 0.04, 0.85]}/>
            <meshStandardMaterial color={scoreHover || page === 6 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-6-wrapper' 
            position={[1.45, -0.1, -0.2]} 
            onClick={handleScoreClick}
            onPointerEnter={handleScorePointerEnter}
            onPointerLeave={handleScorePointerLeave}
          >
            <boxGeometry args={[3.35, 0.05, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[1.45, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab7Winning[language]}
              <meshStandardMaterial color={scoreHover || page === 6 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
        <group name='tab-7' position={[2.8,0,1.6]} scale={0.8}>
          <mesh position={[0.8, -0.1, -0.2]}>
            <boxGeometry args={[2.0, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[0.8, -0.1, -0.2]}>
            <boxGeometry args={[2.1, 0.04, 0.85]}/>
            <meshStandardMaterial color={tipsHover || page === 7 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-7-wrapper' 
            position={[0.8, -0.1, -0.2]} 
            onClick={handleTipsClick}
            onPointerEnter={handleTipsPointerEnter}
            onPointerLeave={handleTipsPointerLeave}
          >
            <boxGeometry args={[2.1, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Center position={[0.8, -0.05, -0.2]} rotation={[-Math.PI/2, 0, 0]}>
            <Text3D
              font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
              size={0.4}
              height={0.01}
            >
              {translations.howToPlayHome.tab8Tips[language]}
              <meshStandardMaterial color={tipsHover || page === 7 ? 'green' : 'orange'}/>
            </Text3D>
          </Center>
        </group>
      </group>
    } else {
      return <group name='tabs' position={position} scale={scale}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.4}
          height={0.01}
          position={[-0.1, 0.5, -0.5]}
        >
          {translations.howToPlayHome.contents[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>
        <group name='tab-0' position={[0,0,0]} scale={0.8}>
          <mesh position={[0.9, -0.1, -0.2]}>
            <boxGeometry args={[2.1, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[0.9, -0.1, -0.2]}>
            <boxGeometry args={[2.2, 0.04, 0.85]}/>
            <meshStandardMaterial color={overviewHover || page === 0 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-0-wrapper' 
            position={[0.9, -0.1, -0.2]}
            onClick={handleOverviewClick}
            onPointerEnter={handleOverviewPointerEnter}
            onPointerLeave={handleOverviewPointerLeave}
          >
            <boxGeometry args={[2.2, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab1Goal[language]}
            <meshStandardMaterial color={overviewHover || page === 0 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-1' position={[0,0,0.8]} scale={0.8}>
          <mesh position={[2.3, -0.1, -0.2]}>
            <boxGeometry args={[4.9, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[2.3, -0.1, -0.2]}>
            <boxGeometry args={[5, 0.04, 0.85]}/>
            <meshStandardMaterial color={throwTheYutHover || page === 1 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-1-wrapper' 
            position={[2.3, -0.1, -0.2]}
            onClick={handleThrowTheYutClick}
            onPointerEnter={handleThrowTheYutPointerEnter}
            onPointerLeave={handleThrowTheYutPointerLeave}
          >
            <boxGeometry args={[5, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab2ThrowTheYut[language]}
            <meshStandardMaterial color={throwTheYutHover || page === 1 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-2' position={[0,0,1.6]} scale={0.8}>
          <mesh position={[2.05, -0.1, -0.2]}>
            <boxGeometry args={[4.4, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[2.05, -0.1, -0.2]}>
            <boxGeometry args={[4.5, 0.04, 0.85]}/>
            <meshStandardMaterial color={readTheDiceHover || page === 2 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-2-wrapper' 
            position={[2.05, -0.1, -0.2]}
            onClick={handleReadTheDiceClick}
            onPointerEnter={handleReadTheDicePointerEnter}
            onPointerLeave={handleReadTheDicePointerLeave}
          >
            <boxGeometry args={[4.5, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab3ReadTheYut[language]}
            <meshStandardMaterial color={readTheDiceHover || page === 2 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-3' position={[0,0,2.4]} scale={0.8}>
          <mesh position={[1.7, -0.1, -0.2]}>
            <boxGeometry args={[3.7, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[1.7, -0.1, -0.2]}>
            <boxGeometry args={[3.8, 0.04, 0.85]}/>
            <meshStandardMaterial color={shortcutHover || page === 3 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-3-wrapper' 
            position={[1.7, -0.1, -0.2]}
            onClick={handleShortcutClick}
            onPointerEnter={handleShortcutPointerEnter}
            onPointerLeave={handleShortcutPointerLeave}
          >
            <boxGeometry args={[3.8, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab4Movement[language]}
            <meshStandardMaterial color={shortcutHover || page === 3 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-4' position={[0,0,3.2]} scale={0.8}>
          <mesh position={[2.15, -0.1, -0.2]}>
            <boxGeometry args={[4.6, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[2.15, -0.1, -0.2]}>
            <boxGeometry args={[4.7, 0.04, 0.85]}/>
            <meshStandardMaterial color={catchEnemiesHover || page === 4 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-4-wrapper' 
            position={[2.15, -0.1, -0.2]}
            onClick={handleCatchEnemiesClick}
            onPointerEnter={handleCatchEnemiesPointerEnter}
            onPointerLeave={handleCatchEnemiesPointerLeave}
          >
            <boxGeometry args={[4.7, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab5CatchEnemies[language]}
            <meshStandardMaterial color={catchEnemiesHover || page === 4 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-5' position={[0,0,4]} scale={0.8}>
          <mesh position={[1.75, -0.1, -0.2]}>
            <boxGeometry args={[3.8, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[1.75, -0.1, -0.2]}>
            <boxGeometry args={[3.9, 0.04, 0.85]}/>
            <meshStandardMaterial color={piggybackHover || page === 5 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-5-wrapper' 
            position={[1.7, -0.1, -0.2]}
            onClick={handlePiggybackClick}
            onPointerEnter={handlePiggybackPointerEnter}
            onPointerLeave={handlePiggybackPointerLeave}
          >
            <boxGeometry args={[3.8, 0.1, 0.7]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab6Piggyback[language]}
            <meshStandardMaterial color={piggybackHover || page === 5 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-6' position={[-0.025, 0, 4.8]} scale={0.8}>
          <mesh position={[1.5, -0.1, -0.2]}>
            <boxGeometry args={[3.25, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[1.5, -0.1, -0.2]}>
            <boxGeometry args={[3.35, 0.04, 0.85]}/>
            <meshStandardMaterial color={scoreHover || page === 6 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-6-wrapper' 
            position={[1.5, -0.1, -0.2]} 
            onClick={handleScoreClick}
            onPointerEnter={handleScorePointerEnter}
            onPointerLeave={handleScorePointerLeave}
          >
            <boxGeometry args={[3.35, 0.1, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab7Winning[language]}
            <meshStandardMaterial color={scoreHover || page === 6 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
        <group name='tab-7' position={[0,0,5.6]} scale={0.8}>
          <mesh position={[0.8, -0.1, -0.2]}>
            <boxGeometry args={[1.9, 0.05, 0.75]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh position={[0.8, -0.1, -0.2]}>
            <boxGeometry args={[2.0, 0.04, 0.85]}/>
            <meshStandardMaterial color={tipsHover || page === 7 ? 'green' : 'orange'}/>
          </mesh>
          <mesh 
            name='tab-7-wrapper' 
            position={[0.8, -0.1, -0.2]}
            onClick={handleTipsClick}
            onPointerEnter={handleTipsPointerEnter}
            onPointerLeave={handleTipsPointerLeave}
          >
            <boxGeometry args={[2.0, 0.05, 0.85]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.4}
            height={0.01}
          >
            {translations.howToPlayHome.tab8Tips[language]}
            <meshStandardMaterial color={tipsHover || page === 7 ? 'green' : 'orange'}/>
          </Text3D>
        </group>
      </group>
    }
  }

  function Pagination({ position, scale }) {

    function handlePageLeft() {
      setPage(page => {
        if (page === 0) {
          return 7
        } else {
          return page-1
        }
      })
    }

    function handlePageRight() {
      setPage(page => {
        if (page === 7) {
          return 0
        } else {
          return page+1
        }
      })
    }
    
    function handlePage0(e) {
      e.stopPropagation()
      setPage(0)
      setTabClicked(true)
    }
    function handlePage1(e) {
      e.stopPropagation()
      setPage(1)
      setTabClicked(true)
    }
    function handlePage2(e) {
      e.stopPropagation()
      setPage(2)
      setTabClicked(true)
    }
    function handlePage3(e) {
      e.stopPropagation()
      setPage(3)
      setTabClicked(true)
    }
    function handlePage4(e) {
      e.stopPropagation()
      setPage(4)
      setTabClicked(true)
    }
    function handlePage5(e) {
      e.stopPropagation()
      setPage(5)
      setTabClicked(true)
    }
    function handlePage6(e) {7
      e.stopPropagation()
      setPage(6)
      setTabClicked(true)
    }
    function handlePage7(e) {
      e.stopPropagation()
      setPage(7)
      setTabClicked(true)
    }

    const space = layout[device].howToPlay.pagination.elementSpace
    const startX = layout[device].howToPlay.pagination.startX
    return <group name='pagination' position={position} scale={scale}>
      <mesh position={[startX, 0, 6]} rotation={[0, 0, Math.PI/2]} onPointerUp={handlePageLeft}>
        <coneGeometry args={[layout[device].howToPlay.pagination.arrowRadius, layout[device].howToPlay.pagination.arrowHeight, 3]}/>
        <meshStandardMaterial color="orange"/>
      </mesh>
      <group name='page-0-button'>
        <mesh 
        position={[startX + space*1, 0, 6]} 
        onPointerUp={e=>handlePage0(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 0 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*1, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*1, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 0 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-1-button'>
        <mesh 
        position={[startX + space*2, 0, 6]} 
        onPointerUp={e=>handlePage1(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 1 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*2, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*2, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 1 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-2-button'>
        <mesh 
        position={[startX + space*3, 0, 6]} 
        onPointerUp={e=>handlePage2(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 2 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*3, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*3, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 2 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-3-button'>
        <mesh 
        position={[startX + space*4, 0, 6]} 
        onPointerUp={e=>handlePage3(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 3 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*4, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*4, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 3 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-4-button'>
        <mesh 
        position={[startX + space*5, 0, 6]} 
        onPointerUp={e=>handlePage4(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 4 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*5, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*5, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 4 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-5-button'>
        <mesh 
        position={[startX + space*6, 0, 6]} 
        onPointerUp={e=>handlePage5(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 5 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*6, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*6, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 5 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-6-button'>
        <mesh 
        position={[startX + space*7, 0, 6]} 
        onPointerUp={e=>handlePage6(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 6 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*7, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*7, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 6 ? "green" : "orange" }/>
        </mesh>
      </group>
      <group name='page-7-button'>
        <mesh 
        position={[startX + space*8, 0, 6]} 
        onPointerUp={e=>handlePage7(e)}
        scale={[0.6, 0.01, 0.6]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 7 ? "green" : "orange" }/>
        </mesh>
        <mesh 
        position={[startX + space*8, 0, 6]} 
        scale={[0.5, 0.02, 0.5]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color='black' transparent opacity={1}/>
        </mesh>
        <mesh 
        position={[startX + space*8, 0, 6]} 
        scale={[0.2, 0.03, 0.2]}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial color={ page === 7 ? "green" : "orange" }/>
        </mesh>
      </group>
      <mesh position={[startX + space*9, 0, 6]} rotation={[0, 0, -Math.PI/2]} onPointerUp={handlePageRight}>
        <coneGeometry args={[layout[device].howToPlay.pagination.arrowRadius, layout[device].howToPlay.pagination.arrowHeight, 3]}/>
        <meshStandardMaterial color="orange"/>
      </mesh>
    </group>
  }

  function CloseButton({position, scale}) {
    const rulebookCloseButtonWrapper = useRef();
    const [rulebookCloseButtonHover, setRulebookCloseButtonHover] = useState(false);
    function handleRulebookCloseButtonPointerEnter() {
      setRulebookCloseButtonHover(true)
    }
    function handleRulebookCloseButtonPointerLeave() {
      setRulebookCloseButtonHover(false)
    }
    function handleRulebookCloseButtonPointerClick() {
      setShowRulebook(false)
    }

    return <group 
      name='rulebook-close-button' 
      position={position}
      scale={scale}
    >
      <mesh rotation={[Math.PI/2, 0, Math.PI/4]}>
        <capsuleGeometry args={[0.06, 0.35, 4, 32]}/>
        <meshStandardMaterial color={ rulebookCloseButtonHover ? 'green' : 'orange' }/>
      </mesh>
      <mesh rotation={[Math.PI/2, 0, -Math.PI/4]}>
        <capsuleGeometry args={[0.06, 0.35, 4, 32]}/>
        <meshStandardMaterial color={ rulebookCloseButtonHover ? 'green' : 'orange' }/>
      </mesh>
      <group name='rulebook-close-button'>
        <mesh>
          <boxGeometry args={[0.6, 0.02, 0.6]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
        <mesh>
          <boxGeometry args={[0.7, 0.01, 0.7]}/>
          <meshStandardMaterial color={ rulebookCloseButtonHover ? 'green' : 'orange' }/>
        </mesh>
        <mesh 
          name='rulebook-close-button-wrapper' 
          ref={rulebookCloseButtonWrapper}
          onPointerEnter={handleRulebookCloseButtonPointerEnter}
          onPointerLeave={handleRulebookCloseButtonPointerLeave}
          onClick={handleRulebookCloseButtonPointerClick}
        >
          <boxGeometry args={[0.7, 0.02, 0.7]}/>
          <meshStandardMaterial color='white' transparent opacity={0}/>
        </mesh>
      </group>
    </group>
  }

  function TipsPage() {
    return <group name='tips-for-beginners'
      position={layout[device].howToPlay.tipsPage.tipsForBeginners.position}>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-3.5, 0, -3]}
        rotation={[-Math.PI/2, 0, 0]}
        size={0.4}
        height={0.01}
      >
        {translations.howToPlayHome.tipsForBeginners[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[-3.5, 0, -2]}
        rotation={[-Math.PI/2, 0, 0]}
        size={0.4}
        height={0.01}
      >
        {translations.howToPlayHome.tip1[language]}
        {'\n\n'}
        {translations.howToPlayHome.tip2[language]}
        {'\n\n'}
        {translations.howToPlayHome.tip3[language]}
        {'\n\n'}
        {translations.howToPlayHome.tip4[language]}
        <meshStandardMaterial color='limegreen'/>
      </Text3D>
    </group>
  }

  const pages = [<Overview/>, <ThrowTheYutPage/>, <ReadTheYutPage/>, <MovementPage/>, <CatchEnemiesPage/>, <PiggybackPage/>, <ScorePage/>,  <TipsPage/>]

  return <group position={position} rotation={rotation} scale={scale}>
    { pages[page] }
    { device === 'portrait' && <Pagination 
      position={layout[device].howToPlay.pagination.position}
      scale={layout[device].howToPlay.pagination.scale}
    /> }
    { device === 'landscapeDesktop' && tabOrientation === 'bottom' && <Tabs position={[-2.3, 0, 10]} orientation={tabOrientation} scale={1}/> }
    { device === 'landscapeDesktop' && tabOrientation === 'right' && <Tabs position={[9.2, 0, -2]} orientation={tabOrientation} scale={0.9}/> }
    { closeButton && <CloseButton
      position={layout[device].game.rulebook.closeButton.position} 
      scale={layout[device].game.rulebook.closeButton.scale
    }/> }
  </group>
}